import React, { useState } from 'react';
import { useGameLounge } from '../context/GameLoungeContext';
import StationCard from './StationCard';
import KioskModal from './KioskModal';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FaPlaystation } from 'react-icons/fa';

const { FiFilter, FiMonitor } = FiIcons;

function StationManagement() {
  const { state } = useGameLounge();
  const [filter, setFilter] = useState('all');
  const [selectedRoom, setSelectedRoom] = useState('all');
  const [selectedStation, setSelectedStation] = useState(null);

  const getAllStations = () => {
    return state.rooms.flatMap(room => 
      room.stations.map(station => ({ ...station, roomName: room.name, roomId: room.id }))
    );
  };

  const filteredStations = getAllStations().filter(station => {
    const matchesFilter = 
      filter === 'all' ||
      (filter === 'occupied' && station.status === 'occupied') ||
      (filter === 'available' && station.status === 'available');
    
    const matchesRoom = selectedRoom === 'all' || station.roomId === selectedRoom;
    
    return matchesFilter && matchesRoom;
  });

  const filterOptions = [
    { value: 'all', label: 'Alle Stationen' },
    { value: 'occupied', label: 'Belegt' },
    { value: 'available', label: 'Verfügbar' }
  ];

  const handleOpenKiosk = (station) => {
    setSelectedStation(station);
  };

  const handleCloseKiosk = () => {
    setSelectedStation(null);
  };

  const allStations = getAllStations();
  const occupiedCount = allStations.filter(s => s.status === 'occupied').length;
  const pcCount = allStations.filter(s => s.type === 'pc').length;
  const psCount = allStations.filter(s => s.type === 'playstation').length;
  const totalRevenue = allStations
    .filter(s => s.status === 'occupied')
    .reduce((sum, s) => sum + s.currentAmount, 0);

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Stationsverwaltung</h1>
        <p className="text-gray-600">Verwalten Sie alle Gaming-Stationen und Sessions</p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Gesamt PCs</p>
              <p className="text-2xl font-bold text-gray-900">
                {pcCount}
              </p>
            </div>
            <SafeIcon icon={FiMonitor} className="text-3xl text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Gesamt PS</p>
              <p className="text-2xl font-bold text-gray-900">
                {psCount}
              </p>
            </div>
            <SafeIcon icon={FaPlaystation} className="text-3xl text-blue-700" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Gesamtumsatz</p>
              <p className="text-2xl font-bold text-green-600">
                {totalRevenue.toFixed(2)} {state.settings.currency}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Räume</p>
              <p className="text-2xl font-bold text-gray-900">{state.rooms.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="flex items-center space-x-4">
          <SafeIcon icon={FiFilter} className="text-gray-500" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {filterOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <select
          value={selectedRoom}
          onChange={(e) => setSelectedRoom(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Alle Räume</option>
          {state.rooms.map(room => (
            <option key={room.id} value={room.id}>
              {room.name}
            </option>
          ))}
        </select>
      </div>

      {/* Stations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredStations.map(station => (
          <StationCard 
            key={station.id} 
            station={station}
            roomName={station.roomName}
            onOpenKiosk={handleOpenKiosk}
          />
        ))}
      </div>

      {filteredStations.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">Keine Stationen gefunden für den ausgewählten Filter.</p>
        </div>
      )}

      {/* Kiosk Modal */}
      {selectedStation && (
        <KioskModal 
          station={selectedStation} 
          onClose={handleCloseKiosk}
        />
      )}
    </div>
  );
}

export default StationManagement;